// View chips in spec order. 'never_bought' shows only where the retailer's size-insertion flag is
// on (an inserted size must really mean "never bought"); see visibleViews.
export const VIEWS = [
  { key: 'all', label: 'All products' },
  { key: 'still_wanted', label: 'Sold out, still wanted' },
  { key: 'never_bought', label: 'Sizes you never bought in to' },
  { key: 'only_you', label: 'Sizes only you have' },
  { key: 'discount_timing', label: 'Discount timing' },
];

export const visibleViews = (sizeInsertionEnabled) => VIEWS.filter((v) => v.key !== 'never_bought' || sizeInsertionEnabled);

// "Sold out for N days or more": any whole number, default 14 (Daniel 2026-10-14).
export const N_DEFAULT = 14;
export const N_MIN = 1;
export const N_MAX = 180;
export const PAGE_SIZE = 50;
export const WIDE_BREAKPOINT = 860; // measured on the content area, not the window (spec §0.6)
export const HISTORY_START = '2026-06-15';
export const MATCHES_FROM = '2026-07-03';

export const KEY_STATUSES = ['in_stock', 'sold_out', 'only_you', 'never_bought'];

export const PERIOD_TOOLTIP =
  'Matches, clicks, orders, stock and price history are counted between these dates. Size status is as of the end date.';
export const END_HELPER = 'Data is complete up to yesterday. Today is added overnight.';
export const START_HELPER = 'Stock history starts 15 Jun 2026';
export const MATCHES_HELPER = 'Match counts start 3 Jul 2026';
export const LIVE_TOOLTIP =
  'Filters by the date a product first went live with you — e.g. set a go-live window to look at one season.';
export const LIVE_INITIAL_IMPORT_NOTE = 'Products from your first import have no go-live date and are only shown under Any time.';
export const BANNER_TEXT =
  "You're new to the network — the picture is still building. These numbers are real but early; brand, size and colour patterns get more reliable after about 60 days of data.";

// Sale filter (sale-filter spec §2, §4): which DAYS count, by the retailer's own price that day.
export const SALE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'full_price', label: 'Full price only' },
  { value: 'on_sale', label: 'On sale' },
];
export const SALE_TOOLTIP =
  'Counts only the days your product was at that price. A day is on sale when your price was below the regular price. ' +
  'Full price only leaves out your sale days; a product keeps its full-price days even if it went on sale later. ' +
  "Partners' prices are not used.";
export const SALE_TIMING_EMPTY = 'Your markdowns are not shown under Full price only. Set Sale to All or On sale to see them.';

// "Sold out at" (sold-out-at spec §2, §4): which SIZES are listed, by the retailer's own price on the
// last day the size was in stock. The retailer splits Discounted with their own cut points
// (Mixpanel-style numeric buckets), saved per retailer so Make and Claude use the same buckets.
export const SOLD_OUT_AT_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'full_price', label: 'Full price' },
  { value: 'discounted', label: 'Discounted' },
];
export const SOLD_OUT_AT_TOOLTIP =
  'The price your size sold out at. We look at your price on the last day the size was in stock. ' +
  'Full price: no discount that day. Discounted: the % off that day. You can split Discounted into your own bands. ' +
  'Unknown: the size was already sold out when our data starts.';
export const SOLD_OUT_AT_NOTE = 'Not used in this view';
export const SOLD_OUT_AT_IGNORED_VIEWS = ['only_you', 'never_bought', 'discount_timing'];
export const BANDS_MAX = 3;
export const OFF_MIN = 1;
export const OFF_MAX = 99;
export const BANDS_SAVED_NOTE = 'Saved for your Monday report and your AI assistant too.';
export const DEPTH_SOLD_OUT_AT_NOTE = 'Depth compares days online. Sold out at is not used here.';
// "Weeks before your sale" (spec §2.4): the rule and the brand's date are always shown next to the figure.
export const saleStartTooltip = (brand, date) =>
  'Your sale for a brand starts on the first day at least a quarter of that brand’s products in your shop were discounted.' +
  (brand && date ? ` Here: ${brand}, ${date}.` : '');

// Product status filter: listed on the retailer's site now, or not.
export const PRODUCT_STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Discount timing lists one group at a time (Daniel 2026-10-05); it opens on Partners discounted first,
// so that chip comes first.
export const TIMING_GROUPS = [
  { key: 'partners_first', label: 'Partners discounted first' },
  { key: 'you_first', label: 'You discounted first' },
  { key: 'same_week', label: 'Same week' },
  { key: 'only_you', label: 'Only you discounted' },
];
export const DEFAULT_TIMING_GROUP = 'partners_first';

// Brands tab views (spec §1.2). The two Thinner counts arrive with the second (depth) request.
export const BRAND_VIEWS = [
  { key: 'all', label: 'All carried brands' },
  { key: 'thinner_sizes', label: 'Thinner on sizes' },
  { key: 'thinner_colours', label: 'Thinner on colours' },
  { key: 'send_more', label: 'You send more' },
  { key: 'receive_more', label: 'You receive more' },
  { key: 'not_carried', label: "Brands you don't carry" },
];

export const BALANCE_TOOLTIP =
  'Left: partners sent you more shoppers. Right: you sent partners more shoppers. Middle: about even.';
export const TIMING_TOOLTIP = 'Compared with every partner carrying the same product';
export const NOT_CARRIED_NOTE = "Not used in Brands you don't carry";
export const DEPTH_VIEWS = ['thinner_sizes', 'thinner_colours'];
export const DEPTH_FLOOR_NOTE = (what) => `${what} with fewer than 2 of your styles or 3 partner styles are not compared.`;
